import { useRef, useEffect } from "react";

import { Box, Flex, IconButton, useDisclosure } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useIntl } from "react-intl";
import { Outlet, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { PageName } from "analytics/events";
import { HEIGHT } from "app/components/Header";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { AscDscIcon } from "ui/AscDscIcon/AscDscIcon";
import { Badge } from "ui/Badge/Badge";
import { Button } from "ui/Button/Button";
import {
  FileBlankListIcon,
  FileBlankLockIcon,
  FilterIcon,
  PackageBoxAddIcon,
} from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TabList } from "ui/Tabs/TabList";
import { TabPanel } from "ui/Tabs/TabPanel";
import { TitleXS } from "ui/Typography/Typography";

import { RestockingFiltersBottomSheet } from "../../components/RestockingFiltersBottomSheet";
import { RestockingSortBottomSheet } from "../../components/RestockingSortBottomSheet";
import { RestockingSortIcon } from "../../components/RestockingSortIcon";
import { usePrivateRestockingList } from "../../services/restockingService/service";
import { useRestockingProductSearchStore } from "../../stores/restockingProductSearchStore";
import { useRestockingServiceStore } from "../../stores/restockingServiceStore";

export function RestockingListPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pageName, withTabList } = useLayoutStore(
    useShallow((state) => ({
      pageName: state.pageName,
      withTabList: state.withTabList,
    })),
  );
  const {
    isOpen: isFiltersBottomSheetOpen,
    onOpen: openFiltersBottomSheet,
    onClose: closeFiltersBottomSheet,
  } = useDisclosure();

  const {
    isOpen: isSortBottomSheetOpen,
    onOpen: openSortBottomSheet,
    onClose: closeSortBottomSheet,
  } = useDisclosure();

  const { isFeatureEnabled: isRestockingV2Enabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_RESTOCKING_V2,
  );
  const isPrivateListPage = pageName === PageName.INVENTORY_PAGE_PRIVATE_LIST;
  const isPublicListPage = pageName === PageName.INVENTORY_PAGE_PUBLIC_LIST;
  const isMoveItemsPage = pageName === PageName.INVENTORY_PAGE_MOVE_ITEMS;

  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const publicRestockingItemCount = useAppBackgroundEffectsStore(
    (state) => state.publicRestockingItemCount,
  );
  const {
    isLoadingPrivateRestockingList,
    filteredTranslatedRestockingItems,
    notDoneRestockingItems,
  } = usePrivateRestockingList();
  const restockingItemsSku = filteredTranslatedRestockingItems?.map((item) => item.sku) || [];
  const notDoneRestockingItemsSku =
    notDoneRestockingItems?.map((item: { sku: string }) => item.sku) || [];

  const {
    totalRestockingItemCount,
    selectedItemsBeingRemoved,
    updateSelectedItemsForRemoval,
    categoryFilters,
    shelfFilters,
    priorityFilters,
    updateCategoryFilters,
    updateShelfFilters,
    updatePriorityFilters,
    sortOrder,
    setSortOrder,
    setSortBy,
    toggleSortOrder,
  } = useRestockingServiceStore(
    useShallow((state) => ({
      totalRestockingItemCount: state.totalRestockingItemCount,
      selectedItemsBeingRemoved: state.selectedItemsBeingRemoved,
      updateSelectedItemsForRemoval: state.updateSelectedItemsForRemoval,
      categoryFilters: state.categoryFilters,
      shelfFilters: state.shelfFilters,
      priorityFilters: state.priorityFilters,
      updateCategoryFilters: state.updateCategoryFilters,
      updateShelfFilters: state.updateShelfFilters,
      updatePriorityFilters: state.updatePriorityFilters,
      sortOrder: state.sortOrder,
      setSortOrder: state.setSortOrder,
      setSortBy: state.setSortBy,
      toggleSortOrder: state.toggleSortOrder,
    })),
  );

  const hasActiveFilters =
    categoryFilters.length > 0 || shelfFilters.length > 0 || priorityFilters.length > 0;

  const setInitialSearchQuery = useRestockingProductSearchStore(
    (state) => state.setInitialSearchQuery,
  );

  const goToTextSearch = () => {
    setInitialSearchQuery(null);
    navigate(routes.inventory.restockingList.textSearch);
  };

  const tabs = [
    {
      label: intl.formatMessage({
        id: "flows.inventory.restocking-list-pages.navigation-tab.private-list",
      }),
      icon: <FileBlankLockIcon />,
      count: totalRestockingItemCount,
      isActive: isPrivateListPage,
      onClick: () => navigate(routes.inventory.restockingList.root),
    },
    {
      label: intl.formatMessage({
        id: "flows.inventory.restocking-list-pages.navigation-tab.shared-list",
      }),
      icon: <FileBlankListIcon />,
      count: publicRestockingItemCount,
      isActive: isPublicListPage,
      onClick: () => navigate(routes.inventory.restockingList.publicList),
    },
  ];

  useEffect(() => {
    if (isRestockingV2Enabled) {
      setSortBy("priority");
      setSortOrder("desc");
    } else {
      setSortBy("shelf");
      setSortOrder("asc");
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!withTabList) return <Outlet />;

  return (
    <Page isFull isBgGrey height="100%">
      <NavigationHeader
        title={intl.formatMessage({
          id: "flows.inventory.restocking-list-pages.navigation-header",
        })}
        onClickGoBack={() => {
          if (isMoveItemsPage) {
            navigate(routes.inventory.restockingList.root);
          } else {
            updateCategoryFilters([]);
            updateShelfFilters([]);
            updatePriorityFilters([]);
            navigate(routes.inventory.root);
          }
        }}
        rightElement={
          <Flex gap="s100">
            {isPrivateListPage && (
              <IconButton
                data-testid="go-to-seach-icon"
                size="xs"
                variant="ghost"
                aria-label="Go to text search"
                icon={<PackageBoxAddIcon boxSize="24px" />}
                onClick={goToTextSearch}
              />
            )}
            {isRestockingV2Enabled ? (
              <IconButton
                data-testid="change-sort-icon"
                size="xs"
                variant="ghost"
                aria-label="Change sort"
                icon={<RestockingSortIcon boxSize="24px" />}
                onClick={openSortBottomSheet}
              />
            ) : (
              <AscDscIcon
                data-testid="asc-dsc-icon"
                sortState={sortOrder}
                onClick={toggleSortOrder}
                cursor="pointer"
                width="24px"
                height="24px"
              />
            )}
            <Box position="relative" w="24px" h="24px">
              <IconButton
                size="xs"
                variant="ghost"
                aria-label="Filter"
                icon={<FilterIcon boxSize="24px" />}
                onClick={openFiltersBottomSheet}
              />
              {hasActiveFilters && (
                <Badge
                  size="medium"
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  pointerEvents="none"
                />
              )}
            </Box>
          </Flex>
        }
      />
      <Flex w="100%" h="100%" maxH={`calc(100% - ${HEIGHT})`} flexDirection="column">
        {isMoveItemsPage ? (
          <Flex
            alignItems="center"
            bg="white"
            h="48px"
            w="100%"
            borderBottomWidth="1px"
            borderBottomColor="grey.200"
            px="s200"
          >
            {selectedItemsBeingRemoved.length > 0 ? (
              <Button
                variant="secondary"
                size="xs"
                onClick={() => updateSelectedItemsForRemoval([])}
                borderRadius="24px"
                px="s150"
                py="s50"
                data-testid="select-all-item-tab"
              >
                <TitleXS color="pinkFlink.500">
                  {intl.formatMessage(
                    {
                      id: "flows.inventory.restocking-list-pages.navigation-tab.selected-items",
                    },
                    { amount: selectedItemsBeingRemoved.length },
                  )}
                </TitleXS>
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="xs"
              onClick={() => {
                if (!isEqual(selectedItemsBeingRemoved, restockingItemsSku)) {
                  updateSelectedItemsForRemoval(restockingItemsSku);
                }
              }}
              borderRadius="24px"
              px="s150"
              py="s50"
              ml="auto"
            >
              <TitleXS color="white">
                {intl.formatMessage({
                  id: "flows.inventory.restocking-list-pages.navigation-tab.select-items",
                })}
              </TitleXS>
            </Button>
          </Flex>
        ) : (
          <TabList
            tabs={tabs}
            activeTabHighlightPosition={tabs.findIndex((tab) => tab.isActive)}
            highlightColor="pinkFlink.500"
            {...(notDoneRestockingItemsSku.length > 0 && isPrivateListPage
              ? {
                  onActionClick: () => navigate(routes.inventory.restockingList.moveItems),
                  actionLabel: intl.formatMessage({
                    id: "flows.inventory.restocking-list-pages.navigation-tab.move-items",
                  }),
                }
              : {})}
            maxW={123}
            minW={123}
          />
        )}
        <TabPanel scrollContainerRef={scrollContainer} isLoading={isLoadingPrivateRestockingList}>
          <Outlet />
        </TabPanel>
      </Flex>
      <RestockingFiltersBottomSheet
        isOpen={isFiltersBottomSheetOpen}
        onClose={closeFiltersBottomSheet}
      />
      <RestockingSortBottomSheet isOpen={isSortBottomSheetOpen} onClose={closeSortBottomSheet} />
    </Page>
  );
}
